import React from 'react';

import {
    downloadEvaluationPdf,
    getIncomeTaxEvaluationDetails,
    getVatEvaluationDetails,
    triggerIncomeTax,
    triggerVatAdvance,
    triggerVatAnnual,
    triggerVatQuarter,
} from '../../../api';
import { MONTHS, QUARTERS } from './evaluationConstants';

export default function useEvaluationData() {
    const now = new Date();
    const [year, setYear] = React.useState(now.getFullYear().toString());
    const [month, setMonth] = React.useState(String(now.getMonth() + 1));
    const [quarter, setQuarter] = React.useState(String(Math.floor(now.getMonth() / 3) + 1));
    const [loading, setLoading] = React.useState(false);
    const [exporting, setExporting] = React.useState(false);
    const [message, setMessage] = React.useState('');
    const [error, setError] = React.useState('');
    const [data, setData] = React.useState(null);
    const [mode, setMode] = React.useState(null);
    const [selectedMode, setSelectedMode] = React.useState('ust-month');
    const [reportLabel, setReportLabel] = React.useState('');
    const [executedRequest, setExecutedRequest] = React.useState(null);
    const [vatDetails, setVatDetails] = React.useState(null);
    const [vatDetailsLoading, setVatDetailsLoading] = React.useState(false);
    const [vatDetailsError, setVatDetailsError] = React.useState('');
    const [vatDetailsOpen, setVatDetailsOpen] = React.useState(false);
    const vatDetailsRequestIdRef = React.useRef(0);

    const resetVatDetailsState = React.useCallback(() => {
        vatDetailsRequestIdRef.current += 1;
        setVatDetails(null);
        setVatDetailsLoading(false);
        setVatDetailsError('');
        setVatDetailsOpen(false);
    }, []);

    const resolveSelectionLabel = React.useCallback((currentMode, currentYear, currentMonth, currentQuarter) => {
        const normalizedYear = String(currentYear || '').trim();
        if (currentMode === 'ust-month') {
            const monthIndex = Number(currentMonth) - 1;
            const monthLabel = MONTHS[monthIndex] || 'Unbekannter Monat';
            return `${monthLabel} ${normalizedYear}`.trim();
        }
        if (currentMode === 'ust-quarter') {
            const quarterIndex = Number(currentQuarter) - 1;
            const quarterLabel = QUARTERS[quarterIndex]?.label || `${currentQuarter}. Quartal`;
            return `${quarterLabel} ${normalizedYear}`.trim();
        }
        if (currentMode === 'ust-annual' || currentMode === 'est-annual') {
            return normalizedYear;
        }
        if (currentMode === 'est-month') {
            const monthIndex = Number(currentMonth) - 1;
            const monthLabel = MONTHS[monthIndex] || 'Unbekannter Monat';
            return `${monthLabel} ${normalizedYear}`.trim();
        }
        if (currentMode === 'est-quarter') {
            const quarterIndex = Number(currentQuarter) - 1;
            const quarterLabel = QUARTERS[quarterIndex]?.label || `${currentQuarter}. Quartal`;
            return `${quarterLabel} ${normalizedYear}`.trim();
        }
        return normalizedYear;
    }, []);

    const run = async (fn, afterMode, selectedLabel, successMessage) => {
        setError('');
        setMessage('');
        setLoading(true);
        setData(null);
        setMode(null);
        setReportLabel('');
        try {
            const res = await fn();
            setData(res.data);
            setMode(afterMode);
            setReportLabel(selectedLabel);
            setMessage(successMessage || selectedLabel);
            setExecutedRequest({
                mode: afterMode,
                year: Number(year),
                month: (afterMode === 'ust-month' || afterMode === 'est-month') ? Number(month) : undefined,
                quarter: (afterMode === 'ust-quarter' || afterMode === 'est-quarter') ? Number(quarter) : undefined,
            });
            resetVatDetailsState();
        } catch (err) {
            setError(err?.message || 'Fehler bei der Auswertung');
        } finally {
            setLoading(false);
        }
    };

    const handleUSTMonth = () => {
        return run(() => {
            const y = Number(year);
            const m = Number(month);
            if (!y || !m || m < 1 || m > 12) throw new Error('Bitte gültiges Jahr/Monat eingeben.');
            return triggerVatAdvance(y, m);
        }, 'ust-month', resolveSelectionLabel('ust-month', year, month, quarter), `Monatliche USt-Auswertung für ${resolveSelectionLabel('ust-month', year, month, quarter)}.`);
    };

    const handleUSTQuarter = () => {
        return run(() => {
            const y = Number(year);
            const q = Number(quarter);
            if (!y || !q || q < 1 || q > 4) throw new Error('Bitte gültiges Jahr/Quartal eingeben.');
            return triggerVatQuarter(y, q);
        }, 'ust-quarter', resolveSelectionLabel('ust-quarter', year, month, quarter), `Quartalsauswertung für ${resolveSelectionLabel('ust-quarter', year, month, quarter)} aggregiert aus drei Monaten.`);
    };

    const handleUSTAnnual = () => {
        return run(() => {
            const y = Number(year);
            if (!y) throw new Error('Bitte gültiges Jahr eingeben.');
            return triggerVatAnnual(y);
        }, 'ust-annual', resolveSelectionLabel('ust-annual', year, month, quarter), `Jährliche USt-Auswertung für ${resolveSelectionLabel('ust-annual', year, month, quarter)}.`);
    };

    const handleEStAnnual = () => {
        return run(() => {
            const y = Number(year);
            if (!y) throw new Error('Bitte gültiges Jahr eingeben.');
            return triggerIncomeTax({ year: y, period: 'year' });
        }, 'est-annual', resolveSelectionLabel('est-annual', year, month, quarter), `Einkommensteuer-Auswertung für ${resolveSelectionLabel('est-annual', year, month, quarter)}.`);
    };

    const handleEStMonth = () => {
        return run(() => {
            const y = Number(year);
            const m = Number(month);
            if (!y || !m || m < 1 || m > 12) throw new Error('Bitte gültiges Jahr/Monat eingeben.');
            return triggerIncomeTax({ year: y, period: 'month', month: m });
        }, 'est-month', resolveSelectionLabel('est-month', year, month, quarter), `Monatliche Einkommensteuer-Auswertung für ${resolveSelectionLabel('est-month', year, month, quarter)}.`);
    };

    const handleEStQuarter = () => {
        return run(() => {
            const y = Number(year);
            const q = Number(quarter);
            if (!y || !q || q < 1 || q > 4) throw new Error('Bitte gültiges Jahr/Quartal eingeben.');
            return triggerIncomeTax({ year: y, period: 'quarter', quarter: q });
        }, 'est-quarter', resolveSelectionLabel('est-quarter', year, month, quarter), `Quartalsweise Einkommensteuer-Auswertung für ${resolveSelectionLabel('est-quarter', year, month, quarter)}.`);
    };

    const handleRunSelected = () => {
        if (selectedMode === 'ust-month') {
            handleUSTMonth();
            return;
        }
        if (selectedMode === 'ust-quarter') {
            handleUSTQuarter();
            return;
        }
        if (selectedMode === 'ust-annual') {
            handleUSTAnnual();
            return;
        }
        if (selectedMode === 'est-month') {
            handleEStMonth();
            return;
        }
        if (selectedMode === 'est-quarter') {
            handleEStQuarter();
            return;
        }
        handleEStAnnual();
    };

    const handleRefresh = () => {
        if (mode === 'ust-month') handleUSTMonth();
        else if (mode === 'ust-quarter') handleUSTQuarter();
        else if (mode === 'ust-annual') handleUSTAnnual();
        else if (mode === 'est-month') handleEStMonth();
        else if (mode === 'est-quarter') handleEStQuarter();
        else if (mode === 'est-annual') handleEStAnnual();
    };

    const handleDownloadPdf = async () => {
        const effectiveRequest = executedRequest || {
            mode: mode || selectedMode,
            year: Number(year),
            month: ['ust-month', 'est-month'].includes(mode || selectedMode) ? Number(month) : undefined,
            quarter: ['ust-quarter', 'est-quarter'].includes(mode || selectedMode) ? Number(quarter) : undefined,
        };
        const effectiveMode = effectiveRequest.mode;
        const effectiveYear = effectiveRequest.year;
        if (!effectiveMode || !effectiveYear) {
            throw new Error('Bitte zuerst eine Auswertung berechnen.');
        }
        setExporting(true);
        setError('');
        try {
            return await downloadEvaluationPdf({
                mode: effectiveMode,
                year: effectiveYear,
                month: effectiveRequest.month,
                quarter: effectiveRequest.quarter,
            });
        } catch (err) {
            setError(err?.message || 'PDF konnte nicht heruntergeladen werden.');
            return null;
        } finally {
            setExporting(false);
        }
    };

    const handleVatDetailsToggle = React.useCallback(async (expanded) => {
        setVatDetailsOpen(expanded);
        if (!expanded) {
            return;
        }
        if (!executedRequest) {
            return;
        }
        if (vatDetails || vatDetailsLoading) {
            return;
        }
        const requestId = vatDetailsRequestIdRef.current + 1;
        vatDetailsRequestIdRef.current = requestId;
        const detailRequest = executedRequest;
        setVatDetailsLoading(true);
        setVatDetailsError('');
        try {
            const response = ['ust-month', 'ust-quarter', 'ust-annual'].includes(detailRequest.mode)
                ? await getVatEvaluationDetails(detailRequest)
                : await getIncomeTaxEvaluationDetails({
                    year: detailRequest.year,
                    period: detailRequest.mode === 'est-month'
                        ? 'month'
                        : detailRequest.mode === 'est-quarter'
                            ? 'quarter'
                            : 'year',
                    month: detailRequest.month,
                    quarter: detailRequest.quarter,
                });
            if (vatDetailsRequestIdRef.current !== requestId) {
                return;
            }
            setVatDetails(response.data);
        } catch (err) {
            if (vatDetailsRequestIdRef.current !== requestId) {
                return;
            }
            setVatDetailsError(err?.message || 'Einzelaufstellung konnte nicht geladen werden.');
        } finally {
            if (vatDetailsRequestIdRef.current === requestId) {
                setVatDetailsLoading(false);
            }
        }
    }, [executedRequest, vatDetails, vatDetailsLoading]);

    const currentLabel = reportLabel || resolveSelectionLabel(selectedMode, year, month, quarter);

    return {
        year,
        month,
        quarter,
        loading,
        exporting,
        message,
        error,
        data,
        mode,
        selectedMode,
        vatDetails,
        vatDetailsLoading,
        vatDetailsError,
        vatDetailsOpen,
        reportLabel: currentLabel,
        setYear,
        setMonth,
        setQuarter,
        setSelectedMode,
        handleRunSelected,
        handleRefresh,
        handleDownloadPdf,
        handleVatDetailsToggle,
    };
}
